/**
 * RBAC permission-matrix vocabulary shared by the Team page (role editor) and any
 * UI that needs to describe a role. Keys match the backend feature keys exactly.
 */

export const ACTIONS = [
  { key: 'read', label: 'View' },
  { key: 'write', label: 'Create / Edit' },
  { key: 'delete', label: 'Delete' },
];

export const FEATURES = [
  { key: 'customer_organization', label: 'Customer Organizations', description: 'Customer companies, groups and their portal users' },
  { key: 'service_catalog', label: 'Service Catalog', description: 'Internal services, groups, document types, custom fields, mappings' },
  { key: 'work_order', label: 'Work Orders', description: 'Create and process work orders, tasks and delegation' },
  { key: 'quotation', label: 'Quotations', description: 'Draft, send and convert quotations' },
  { key: 'invoice', label: 'Invoices', description: 'Draft, finalize, void and email invoices' },
  { key: 'finance', label: 'Finance', description: 'Wallets, receipts, settlements, tax, bank accounts, ledger' },
  { key: 'subscription', label: 'Subscription', description: 'Plan, usage and billing history' },
  { key: 'settings', label: 'Settings', description: 'Branding, company, invoice, work-order, email, notification and AI settings' },
  { key: 'thaklis_features', label: 'Thaklis Features', description: 'Tickets, Thaklis payments and document expiry', requiresThaklis: true },
  { key: 'connections', label: 'Connections', description: 'B2B provider / client connections', requiresConnections: true },
  { key: 'ai_assistant', label: 'AI Assistant', description: 'Chat with the AI Assistant, see own chat history and report wrong answers (View)' },
];

/** A matrix with every feature present and every action false. */
export const emptyMatrix = () =>
  FEATURES.reduce((m, f) => ({ ...m, [f.key]: { read: false, write: false, delete: false } }), {});

/** Normalise whatever the API returned into a full matrix (missing keys ⇒ false). */
export const normaliseMatrix = (perms) => {
  const base = emptyMatrix();
  if (!perms || typeof perms !== 'object') return base;
  FEATURES.forEach(({ key }) => {
    const p = perms[key] || {};
    base[key] = { read: p.read === true, write: p.write === true, delete: p.delete === true };
  });
  return base;
};

/** Short human summary of a matrix, e.g. "4 features · 2 can edit · 1 can delete". */
export const describeMatrix = (perms) => {
  const m = normaliseMatrix(perms);
  const rows = FEATURES.map((f) => m[f.key]);
  const viewed = rows.filter((p) => p.read).length;
  const edits = rows.filter((p) => p.write).length;
  const deletes = rows.filter((p) => p.delete).length;
  if (viewed === 0) return 'No access';
  const parts = [`${viewed} feature${viewed === 1 ? '' : 's'}`];
  if (edits) parts.push(`${edits} can edit`);
  if (deletes) parts.push(`${deletes} can delete`);
  return parts.join(' · ');
};
